
import { Component, Prop, Vue, Watch } from 'vue-property-decorator';
import ClientProjectsTableWrapper from '@/components/views/ClientEdit/ClientProjectsTableWrapper.vue';
import { ClientTabs } from '@/enums/components/EditClient/ClientTabs';
import CustomerInformation from '@/components/global/client/CustomerInformation.vue';
import ContactInformation from '@/components/global/client/ContactInformation.vue';
import DiscountInformation from '@/components/global/client/DiscountInformation.vue';
import Currency from '@/models/Currency';
import PaymentType from '@/models/PaymentType';
import BankAccount from '@/models/BankAccount';
import Country from '@/models/Country';
import PriceType from '@/models/PriceType';
import PriceListRegion from '@/models/PriceListRegion';
import ContactPerson from '@/models/ContactPerson';
import ContactConfig from '@/models/interfaces/Contact';
import AddressConfig from '@/models/interfaces/Address';
import Address from '@/models/Address';
import Discount from '@/models/Discount';
import Client from '@/models/Client';
import { AvailableClientFields } from '@/interfaces/components/clients/AvailableClientFields';
import ClientTypeRepository from '@/repositories/ClientTypeRepository';
import PaymentTypeRepository from '@/repositories/PaymentTypeRepository';
import ProductGroup from '@/models/ProductGroup';
import { EventBus } from '@/helpers/EventBusHelper';
import { EventBusEvents } from '@/enums/global/EventBusEvents';
import { PopupEvents } from '@/enums/global/PopupEvents';
import CMSUser from '@/models/CMSUser';
import ClientType from '@/models/ClientType';
import { UserRightsEnum } from '@/enums/global/UserRights';
import { ErrorOptions } from '@/interfaces/ErrorOptions';
import { DiscountTableEntry } from '@/interfaces/components/clients/DiscountTableEntry';
import { webpageValidationRegex, emailValidationRegex } from '@/regex/formRegex';
import LeadOrigin from '@/models/LeadOrigin';
import AddressType from '@/models/AddressType';
import { UserRepository } from '@/repositories/UserRepository';
import ClientNotes from '@/components/global/client/ClientNotes.vue';
import UserGroupRepository from '@/repositories/UserGroupRepository';
import AddressTypeRepository from '@/repositories/AddressTypeRepository';

@Component({
    inject: [],
    name: 'EditClientTabs',
    components: {
        CustomerInformation,
        ContactInformation,
        DiscountInformation,
        ClientProjectsTableWrapper,
        ClientNotes,
    },
})
export default class EditClientTabs extends Vue {
    @Prop({ required: true }) private clientId!: string;
    @Prop({ required: true }) private client!: Client;
    @Prop({ required: true }) private formData!: any;
    @Prop({ required: true }) private isItalyUG!: boolean;
    @Prop({ required: true }) private canAccessLogistics!: boolean;

    private emailValidationOverride: boolean = false;
    private emailValidationOverrideValue: boolean = false;
    private isTabLoading = false;
    private deletedDiscountIds: string[] = [];
    private validationErrors: string[] = [];
    private refErrors: any[] = [];
    private isEditingDiscounts = false;
    private activeTab = 'Customer';
    private contactErrors = [];

    private mailFormat = emailValidationRegex;

    private get userRights() {
        return this.$store.getters['jwtData/userRights'];
    }

    public get canUserEditDiscounts() {
        return this.userRights.includes(UserRightsEnum.EDIT_DISCOUNTS);
    }

    private get isBasicClient() {
        return this.client?.isBasic ?? false;
    }

    private get proxyClientTabs() {
        return {
            CUSTOMER: ClientTabs.CUSTOMER,
            CONTACT: ClientTabs.CONTACT,
            PROJECTS: ClientTabs.PROJECTS,
            DISCOUNTS: ClientTabs.DISCOUNTS,
            FAMILY: ClientTabs.FAMILY,
            NOTES: ClientTabs.NOTES,
        };
    }

    private get defaultCountryId() {
        const currentlySelectedCountry = this.formData.clientAddresses[0]?.country;

        if (currentlySelectedCountry) {
            return currentlySelectedCountry;
        }

        let defaultCountry;

        const currentUserGroup = UserGroupRepository.getById(String(this.$store.getters['jwtData/currentUserGroup']));

        if (currentUserGroup?.client) {
            defaultCountry = currentUserGroup.client?.address?.country.id;
        }

        defaultCountry = currentUserGroup?.clientCountryId?.id;

        return defaultCountry;
    }

    private get clientTypes() {
        return ClientTypeRepository.getAll();
    }

    private get paymentTypes() {
        return PaymentTypeRepository.getAllWithArchived();
    }

    private get addressTypes() {
        return AddressTypeRepository.getAll();
    }

    private get displayCustomerType() {
        return this.$t(this.formData.isCompany ? 'Company' : 'Natural person');
    }

    private get contactTab() {
        return this.formData.isCompany ? this.proxyClientTabs.CONTACT : this.proxyClientTabs.FAMILY;
    }

    private get isCurrentUserTechnologist() {
        return UserRepository.isCurrentUserTechnologist();
    }

    private get isClientProtected() {
        return this.client?.isBasic;
    }

    @Watch('client', {
        immediate: true,
    })
    private async onClientChange() {
        await this.$nextTick();

        const clientExists = this.client && Object.keys(this.client as { [key: string]: any }).length;
        this.isTabLoading = !clientExists;
    }

    private setFieldsBorder(
        arr: string[],
        addressErrors: Array<{ ref: string; addressIndex: number }>,
        clear: boolean = false,
    ) {
        let customerInfo = this.$refs.customerinfo as any;

        if (Array.isArray(customerInfo)) {
            customerInfo = customerInfo[0];
        }

        if (!customerInfo) {
            return;
        }

        const borderStyle = clear ? '1px solid #d9d9d9' : '1px solid red';

        arr.forEach((item: string) => {
            if (item === 'phone') {
                const phoneWrapper = document.getElementById('phone-number-wrapper');
                if (phoneWrapper) {
                    phoneWrapper.style.border = borderStyle;
                }
                return;
            }

            // Handle 'name' field which is inside customerTypeSection
            if (item === 'name') {
                const customerTypeSection = customerInfo.$refs.customerTypeSection as any;
                if (customerTypeSection) {
                    const el = customerTypeSection.nameInput || customerTypeSection.$refs?.nameInput;
                    if (el && el.$el) {
                        el.$el.style.border = borderStyle;
                    } else if (el && el.style) {
                        el.style.border = borderStyle;
                    }
                }
                return;
            }

            // Handle 'tax' field which is inside taxesPaymentSection
            if (item === 'tax') {
                const taxesPaymentSection = customerInfo.$refs.taxesPaymentSection as any;
                if (taxesPaymentSection) {
                    const el = taxesPaymentSection.tax || taxesPaymentSection.$refs?.tax;
                    if (el && el.$el) {
                        el.$el.style.border = borderStyle;
                    } else if (el && el.style) {
                        el.style.border = borderStyle;
                    }
                }
                return;
            }

            const el = customerInfo.$refs[item] as any;

            if (el && el instanceof Vue) {
                el.$el.style.border = borderStyle;
            } else if (el && el.$el) {
                el.$el.style.border = borderStyle;
            }
        });

        addressErrors.forEach((item) => {
            const addressFormRef = `addressForm-${item.addressIndex}`;
            let addressForm = customerInfo.$refs[addressFormRef] as any;

            // Handle array refs (Vue wraps v-for refs in arrays)
            if (Array.isArray(addressForm)) {
                addressForm = addressForm[0];
            }

            if (!addressForm) {
                return;
            }

            // Try to find the ref directly on addressForm
            let el = addressForm.$refs[item.ref] as any;

            // If not found, try in addressLocationFields child component
            if (!el && addressForm.$refs.addressLocationFields) {
                const locationFields = addressForm.$refs.addressLocationFields as any;
                if (locationFields[item.ref]) {
                    el = locationFields[item.ref];
                } else if (locationFields.$refs && locationFields.$refs[item.ref]) {
                    el = locationFields.$refs[item.ref];
                }
            }

            // Handle Vue 3 ref objects
            if (el && el.value && el.value.$el) {
                el = el.value;
            }

            if (el && el instanceof Vue) {
                el.$el.style.border = borderStyle;
            } else if (el && el.$el) {
                el.$el.style.border = borderStyle;
            } else if (el && el.style) {
                el.style.border = borderStyle;
            }
        });
    }

    private confirmFieldsAreValid() {
        this.validationErrors = [];
        this.refErrors = [];
        const addressErrors: Array<{ ref: string; addressIndex: number }> = [];

        let flag = true;
        let customerIsValid = true;
        let contactInfoIsValid = true;

        if (!this.formData.name) {
            flag = false;
            customerIsValid = false;
            this.validationErrors.push(this.$t('Polje ime je obavezno'));
            this.refErrors.push('name');
        }

        // Validate clientAddresses array
        if (!this.formData.clientAddresses || this.formData.clientAddresses.length === 0) {
            flag = false;
            customerIsValid = false;
            this.validationErrors.push(this.$t('At least one address is required'));
        } else {
            this.formData.clientAddresses.forEach((address: any, index: number) => {
                if (!address?.streetNameAndNumber) {
                    flag = false;
                    customerIsValid = false;
                    this.validationErrors.push(this.$t('Polje naziv ulice i broj je obavezno') as string);
                    addressErrors.push({ ref: 'addressStreetNameAndNumber', addressIndex: index });
                }

                if (!address?.poNumber) {
                    flag = false;
                    customerIsValid = false;
                    this.validationErrors.push(this.$t('Polje poštanski broj je obavezno') as string);
                    addressErrors.push({ ref: 'addressPoNumber', addressIndex: index });
                }

                if (!address?.city) {
                    flag = false;
                    customerIsValid = false;
                    this.validationErrors.push(this.$t('Polje grad je obavezno') as string);
                    addressErrors.push({ ref: 'addressCity', addressIndex: index });
                }

                if (!address?.country) {
                    flag = false;
                    customerIsValid = false;
                    this.validationErrors.push(this.$t('Polje država je obavezno') as string);
                    addressErrors.push({ ref: 'country', addressIndex: index });
                }
            });
        }

        let missingContactInfo = false;
        let missingAddressInfo = false;

        const contactErrors: any = [];
        this.formData.contacts.forEach((contact: any, i: number) => {
            const isContactValid = !!contact.name && !!contact.email && !!contact.phone;

            const isAddressValid =
                !!contact.address?.streetNameAndNumber &&
                !!contact.address?.poNumber &&
                !!contact.address?.city &&
                !!contact.address?.country;

            if (!isContactValid) {
                missingContactInfo = true;
            }

            if (!isAddressValid) {
                missingAddressInfo = true;
            }

            contactErrors.push({ index: i, errors: this.checkContactFieldsForErrors(contact) });
        });

        this.contactErrors = contactErrors;

        if (missingContactInfo) {
            flag = false;
            contactInfoIsValid = false;
            this.validationErrors.push(this.$t('Polja imena, emaila i telefona su obavezna'));
        }

        if (missingAddressInfo) {
            flag = false;
            contactInfoIsValid = false;
            this.validationErrors.push(this.$t('Polja adrese kontakt osobe su obavezna'));
        }

        // @todo check every contact
        if (!this.formData.contacts.length) {
            flag = false;
            contactInfoIsValid = false;
            this.validationErrors.push(this.$t('Nedostaje kontakt osoba'));
            this.refErrors.push('contacts');
        }

        if (this.formData?.isCompany) {
            const webpageValid = this.validateWebpage();

            if (!webpageValid) {
                flag = false;
                customerIsValid = false;
                this.validationErrors.push(this.$t('Invalid webpage!'));
                this.refErrors.push('webpage');
            }
        }

        if (this.formData.isCompany || this.isItalyUG) {
            if (!this.formData.taxId) {
                flag = false;
                customerIsValid = false;
                this.validationErrors.push(this.$t('Polje porezni id broj je obavezno'));
                this.refErrors.push('tax');
            }
        }

        // Clear previous borders on all fields
        const allClearableRefs = ['name', 'phone', 'tax', 'contacts'];
        const allAddressRefs = ['addressStreetNameAndNumber', 'addressPoNumber', 'addressCity', 'country'];
        const allAddressErrors: Array<{ ref: string; addressIndex: number }> = [];
        if (this.formData.clientAddresses) {
            this.formData.clientAddresses.forEach((_: any, index: number) => {
                allAddressRefs.forEach((ref) => {
                    allAddressErrors.push({ ref, addressIndex: index });
                });
            });
        }
        this.setFieldsBorder(allClearableRefs, allAddressErrors, true);

        // Set red borders on invalid fields
        if (!flag) {
            this.setFieldsBorder(this.refErrors, addressErrors, false);
        }

        this.switchToCorrectTab(customerIsValid, contactInfoIsValid);

        return flag;
    }

    private validateWebpage() {
        const input = this.formData.webpage;

        if (!input) {
            return true;
        }

        if (!webpageValidationRegex.test(input)) {
            return false;
        }

        return true;
    }

    private checkContactFieldsForErrors(contact: any) {
        const errorState: any = {};

        if (!contact.email) {
            errorState.email = true;
        }

        if (!contact.phone) {
            errorState.phone = true;
        }

        if (!contact.name) {
            errorState.name = true;
        }

        if (contact.useDefaultAddress) {
            return errorState;
        }

        if (!contact.address?.city) {
            errorState.city = true;
        }

        if (!contact.address?.country) {
            errorState.country = true;
        }

        if (!contact.address?.poNumber) {
            errorState.poNumber = true;
        }

        if (!contact.address?.streetNameAndNumber) {
            errorState.streetNameAndNumber = true;
        }

        return errorState;
    }

    private switchToCorrectTab(customerIsValid: boolean, contactInfoIsValid: boolean) {
        let contactTab = '';

        if (this.formData.isCompany) {
            contactTab = this.proxyClientTabs.CONTACT;
        } else {
            contactTab = this.proxyClientTabs.FAMILY;
        }

        switch (this.activeTab) {
            case 'Customer':
                if (!customerIsValid) {
                    return;
                }

                if (!contactInfoIsValid) {
                    this.activeTab = contactTab;
                }
                break;
            case contactTab:
                if (!contactInfoIsValid) {
                    return;
                }

                if (!customerIsValid) {
                    this.activeTab = 'Customer';
                }
            default:
                if (!customerIsValid) {
                    this.activeTab = 'Customer';
                }

                if (!contactInfoIsValid) {
                    this.activeTab = contactTab;
                }
        }
    }

    private openDuplicateClientPopup(duplicate: Client) {
        EventBus.$emit(EventBusEvents.openDuplicateClientPopup, {
            popupEvent: PopupEvents.openDuplicateClient,
            data: {
                client: duplicate,
            },
        });
    }

    private async confirmEmailsAreValid() {
        const contactEmails = this.formData.contacts.map((contact: any) => {
            return contact.email;
        });

        const emails = [...contactEmails];
        const clients: Client[] = [];

        for await (const email of emails) {
            if (email) {
                if (!email.match(this.mailFormat)) {
                    this.$notification.error({
                        message: this.$t('Invalid email') as string,
                        description: '',
                    });
                    this.$emit('stopLoading');

                    return false;
                }

                const duplicates = await this.checkDuplicateEmails(email);
                const duplicate = this.findFirstNonselfDuplicate(duplicates);
                if (duplicate) {
                    clients.push(duplicate);

                    this.openDuplicateClientPopup(duplicate);
                    this.$emit('stopLoading');

                    return false;
                }
            }
        }

        if (clients.length === 0) {
            return Promise.resolve(true);
        }
    }

    private findFirstNonselfDuplicate(duplicates: any) {
        if (!duplicates || !duplicates.length) {
            return false;
        }

        const validDuplicate = duplicates.find((duplicate: any) => {
            return duplicate && duplicate.id !== this.client.id;
        });

        return validDuplicate;
    }

    private async checkDuplicateEmails(input: string) {
        const customerName = this.formData.name;
        const customerAddress = this.formData.addressForm.streetNameAndNumber;
        const customerTaxId = this.formData.taxId;
        let clients = null;

        try {
            if (this.formData.isCompany && Array.isArray(this.formData.contacts)) {
                for (const contact of this.formData.contacts) {
                    const result = await Client.validateClient(
                        contact.email,
                        contact.phone,
                        customerName,
                        customerAddress,
                        customerTaxId,
                    );
                    if (result && result.data.length > 0) {
                        return result.data;
                    }
                }
            } else {
                clients = await Client.validateClient(
                    input,
                    this.formData.phone,
                    customerName,
                    customerAddress,
                    customerTaxId,
                );
                return clients.data.length <= 0 ? null : clients.data;
            }
        } catch (e) {
            console.error('Error checking duplicates', e);
            return null;
        }

        return null;
    }

    private isCustomerInfoShown(tab: string) {
        return tab === ClientTabs.CUSTOMER;
    }
    private isContactInfoShown(tab: string) {
        return tab === ClientTabs.CONTACT;
    }
    private isProjectsShown(tab: string) {
        return tab === ClientTabs.PROJECTS;
    }
    private isDiscountsShown(tab: string) {
        return tab === ClientTabs.DISCOUNTS;
    }
    private updateIsEditingDiscounts(val: boolean) {
        this.isEditingDiscounts = val;
    }
    private async updateCachedValues() {
        EventBus.$emit(EventBusEvents.updateClientCachedData);
        await this.$nextTick();
    }
    private async updateClientAndDepenencies({ sendToIngenious = false } = {}) {
        if (this.canUserEditDiscounts && this.isEditingDiscounts) {
            this.$notification.error({
                message: this.$t('Greška') as string,
                description: this.$t(
                    'You are still editing some discounts, please accept or decline changes to save.',
                ) as string,
            });
            return;
        }
        this.$emit('startLoading');

        this.formatDataForUpdate(this.formData);

        if (!this.confirmFieldsAreValid()) {
            this.$notification.error({
                message: this.validationErrors.join('!\n'),
                description: '',
            });

            this.$emit('stopLoading');

            return;
        }

        const emailsAreValid = await this.confirmEmailsAreValid();

        if (!emailsAreValid) {
            this.$emit('stopLoading');

            return;
        }

        await this.updateCachedValues();

        try {
            if (this.canUserEditDiscounts) {
                await this.createDiscounts();
            }

            // Create addresses first because we needs ids for both client addresses and contact person addresses when updating/creating contacts
            await this.createClientAddresses(this.formData);

            this.formData.contactPersons = await this.updateContacts();
            await this.updateClient(this.formData);

            if (sendToIngenious) {
                this.sendToIngenious();
            }
        } catch (e) {
            const error = e as ErrorOptions;

            this.$notification.error({
                message: this.$t('Dogodila se greška') as string,
                description: error.response ? error.response.data.meta.message : error.message,
            });
            this.$emit('stopLoading');
            return;
        }

        this.$notification.success({
            message: this.$t('Client has been updated!') as string,
            description: '',
            class: 'client-update-success',
        });

        this.$emit('clientUpdated');
        this.$emit('stopLoading');
    }

    private async updateClientWithDuplicatedValues() {
        const data = this.formData as AvailableClientFields;
        this.formatDataForUpdate(data);

        try {
            if (this.canUserEditDiscounts) {
                await this.createDiscounts();
            }

            await this.createClientAddresses(this.formData);

            this.formData.contactPersons = await this.updateContacts();
            await this.updateClient(this.formData);
        } catch (e) {
            const error = e as ErrorOptions;

            this.$notification.error({
                message: this.$t('Dogodila se greška') as string,
                description: error.response ? error.response.data.meta.message : error.message,
            });
            this.$emit('stopLoading');
            return;
        }

        this.$notification.success({
            message: this.$t('Client has been updated!') as string,
            description: '',
            duration: 3,
            class: 'client-update-success',
        });

        this.$emit('clientUpdated');
        this.$emit('stopLoading');
    }

    private formatDataForUpdate(data: AvailableClientFields) {
        if (data.addressForm && !data.addressForm.country) {
            data.addressForm.country = this.defaultCountryId;
        }

        if (data.assignedUser && typeof data.assignedUser !== 'string' && data.assignedUser?.id) {
            data.assignedUser = data.assignedUser.id;
        }

        if (typeof data.tags[0] !== 'string') {
            data.tags = data.tags.map((tag: any) => tag.name);
        }

        if (data.discounts.length) {
            for (const discount of data.discounts) {
                if (discount.pc1) {
                    discount.pc1 = parseFloat(discount.pc1);
                } else {
                    discount.pc1 = 0;
                }

                if (discount.pc2) {
                    discount.pc2 = parseFloat(discount.pc2);
                } else {
                    discount.pc2 = 0;
                }
            }
        }

        if (this.formData.clientAddresses?.length) {
            this.formData.clientAddresses.forEach((address: any) => {
                if (Array.isArray(address.b2bDeliveryDays) && address.b2bDeliveryDays.length) {
                    address.b2bDeliveryDays = address.b2bDeliveryDays.join('| ').trim();
                } else if (typeof address.b2bDeliveryDays !== 'string') {
                    address.b2bDeliveryDays = '';
                }
            });
        }

        if (this.formData.contacts?.length) {
            this.formData.contacts.forEach((contact: any) => {
                if (
                    contact.address &&
                    Array.isArray(contact.address.b2bDeliveryDays) &&
                    contact.address.b2bDeliveryDays.length
                ) {
                    contact.address.b2bDeliveryDays = contact.address.b2bDeliveryDays.join('| ').trim();
                } else if (contact.address && typeof contact.address.b2bDeliveryDays !== 'string') {
                    contact.address.b2bDeliveryDays = '';
                }
            });
        }
    }

    private async updateClient(data: AvailableClientFields) {
        if (data.tags.length > 0) {
            data.clientTypes = data.tags;
        }

        data.paymentType = this.findPaymentTypeId(data);

        try {
            await Client.updateExisting(this.clientId, data);
            EventBus.$emit(EventBusEvents.initializeClientAttachments);
        } catch (e) {
            const error = e as ErrorOptions;
            this.$notification.error({
                message: this.$t('Dogodila se greška') as string,
                description: error.response ? error.response.data.meta.message : error.message,
            });

            this.$emit('stopLoading');
            return;
        }

        return Promise.resolve();
    }

    private async createClientAddresses(data: AvailableClientFields) {
        if (!data.clientAddresses || data.clientAddresses.length === 0) {
            return;
        }

        const addressPromises = data.clientAddresses.map(async (address: AddressConfig) => {
            if (address.id) {
                // Update existing address
                const resp = await Address.updateExisting(address.id, address);

                this.mapAddressIdToContacts(address);
                return resp.data.id;
            } else {
                // Create new address and update the address object with the new ID
                const resp = await Address.createNew(address);
                address.id = resp.data.id;

                this.mapAddressIdToContacts(address);
                return resp.data.id;
            }
        });

        const addressIds = await Promise.all(addressPromises);

        // Set addresses array for the API
        data.addresses = addressIds;
    }

    private mapAddressIdToContacts(address: AddressConfig) {
        this.formData.contacts.forEach((contact: ContactConfig) => {
            if (contact.address && contact.useDefaultAddress) {
                if (address.id === contact.address?.id) {
                    return;
                }

                if (!contact.address?._localId || !address?._localId) {
                    return;
                }

                if (contact.address._localId === address._localId) {
                    contact.address.id = address.id;
                }
            }
        });
    }

    private async updateContacts() {
        const contacts = [];

        try {
            for (const contact of this.formData.contacts as ContactConfig[]) {
                // Ignore empty contact form when client is person type
                if (!this.formData.isCompany && (!contact.name || !contact.email || !contact.phone)) {
                    continue;
                }

                if (contact.id) {
                    this.fixContactPersonCountryFormatting(contact);
                    const addressId = contact.address?.id;

                    // Handle address updates/creations for contacts that don't use company address
                    if (!contact.useDefaultAddress && addressId) {
                        await Address.updateExisting(addressId, contact.address!);
                    }

                    if (!contact.useDefaultAddress && !addressId) {
                        const newAddress = await Address.createNew(contact.address!);
                        contact.address_id = newAddress.data.id;
                    }

                    const existingContact = await ContactPerson.updateExisting(contact.id!, contact);

                    contacts.push(existingContact.data.id);
                } else {
                    if (!contact.useDefaultAddress) {
                        const newAddress = await Address.createNew(contact.address!);
                        contact.address_id = newAddress.data.id;
                    }

                    const newContact = await ContactPerson.createNew(contact);

                    contacts.push(newContact.data.id);
                }
            }
        } catch (e) {
            const error = e as ErrorOptions;
            this.$notification.error({
                message: this.$t('Dogodila se greška') as string,
                description: error.response ? error.response.data.meta.message : error.message,
            });

            this.$emit('stopLoading');
            return;
        }

        return contacts;
    }

    private fixContactPersonCountryFormatting(contact: ContactConfig) {
        if (contact.address && contact.address.country) {
            if (typeof contact.address.country === 'string') {
                contact.address.country = contact.address.country.split('|')[0];
            } else if (contact.address.country.id) {
                contact.address.country = contact.address.country.id;
            }
        }
    }

    private async createDiscounts() {
        await this.deleteDiscounts();

        const promises: Array<Promise<any>> = [];
        this.formData.discounts.forEach((discount: DiscountTableEntry) => {
            if (discount.id) {
                promises.push(Discount.updateExisting(discount.id, discount));
            }
        });

        try {
            for (const discount of this.formData.discounts as DiscountTableEntry[]) {
                if (discount.id) {
                    continue;
                }

                // Create new discounts
                discount.clientId = this.clientId;
                const resp = await Discount.createNew(discount);
                discount.id = resp.data.id;
                discount.isEditing = false;
            }

            await Promise.allSettled(promises);
        } catch (e) {
            const error = e as ErrorOptions;
            this.$notification.error({
                message: this.$t('Dogodila se greška') as string,
                description: error.response ? error.response.data.meta.message : error.message,
            });
            this.$emit('stopLoading');
            return;
        }

        return Promise.resolve();
    }

    private async deleteDiscounts() {
        const promises: Array<Promise<any>> = [];

        this.deletedDiscountIds.forEach(async (id: string, i: number) => {
            promises.push(Discount.deleteExisting(id));
        });

        try {
            await Promise.allSettled(promises);
        } catch (e) {
            const error = e as ErrorOptions;
            this.$notification.error({
                message: this.$t('Dogodila se greška') as string,
                description: error.response ? error.response.data.meta.message : error.message,
            });
            this.$emit('stopLoading');
            return;
        }

        this.deleteDiscountsLocally();
        return Promise.resolve();
    }

    private deleteDiscountsLocally() {
        this.formData.discounts = this.formData.discounts.filter(
            (discount: DiscountTableEntry) => !this.deletedDiscountIds.includes(discount.id),
        );

        this.deletedDiscountIds = [];
    }

    private findPaymentTypeId(data: AvailableClientFields) {
        if (!!data.paymentType) {
            return data.paymentType;
        }
        return this.paymentTypes.find((type: any) => type.isDefault)?.id;
    }

    private updateDeletedDiscountIds(id: string) {
        this.deletedDiscountIds.push(id);
    }

    private async sendToIngenious() {
        if (this.clientId == null) {
            return;
        }

        try {
            await Client.sendToIngenious(this.clientId);

            this.$notification.success({
                message: this.$t('Klijent je uspješno poslan u Ingenious!') as string,
                description: '',
            });
        } catch (e) {
            const error = e as ErrorOptions;
            this.$notification.error({
                message: this.$t('Dogodila se greška') as string,
                description: error.response ? error.response.data.meta.message : error.message,
            });
            return;
        }
    }

    private async fetchInitialClientData() {
        try {
            await Promise.allSettled([
                Currency.getAll(),
                PaymentType.getAll(),
                ClientType.getAll(),
                BankAccount.getAll(),
                Country.getAll(),
                PriceType.getAll(),
                PriceListRegion.getAll(),
                ProductGroup.getAll(),
                CMSUser.getAllOnlyUsers(),
                LeadOrigin.getAll(),
                AddressType.getAll(),
            ]);
        } catch (e) {
            Promise.reject(e);
        }
    }

    private logActiveTab(activeTab: any) {
        this.activeTab = activeTab;
    }

    private async created() {
        await this.fetchInitialClientData();
        EventBus.$on(EventBusEvents.updateClient, this.updateClientAndDepenencies);
        EventBus.$on(EventBusEvents.saveClientWithDuplicates, this.updateClientWithDuplicatedValues);
    }

    private beforeDestroy() {
        EventBus.$off(EventBusEvents.updateClient, this.updateClientAndDepenencies);
        EventBus.$off(EventBusEvents.saveClientWithDuplicates, this.updateClientWithDuplicatedValues);
    }
}
